import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import {
    Box, Typography, IconButton, Button, Chip,
    Snackbar, Alert, CircularProgress, Dialog,
    DialogTitle, DialogContent, DialogActions, TextField,
    Menu, MenuItem, Paper, Stack, Tooltip, Divider,
    List, ListItem, ListItemText, ListItemIcon, Badge,
} from "@mui/material";
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    MoreVert as MoreVertIcon,
    ElectricBolt as ElectricBoltIcon,
    AccessTime as AccessTimeIcon,
    Refresh as RefreshIcon,
    ShowChart as ShowChartIcon,
    Devices as DevicesIcon,
    Bolt as BoltIcon,
    CheckCircle as CheckCircleIcon,
    ElectricMeter as ElectricMeterIcon,
    Business as BusinessIcon,
} from "@mui/icons-material";
import { Close as CloseIcon } from "@mui/icons-material";
import { getUserDetails } from "../api/user";
import { getPlantList } from "../api/plant";
import { diagram_meter_data } from "../api/DynamicDiagramapi";

const API_BASE = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem("token");
const ACCENTS = ["#3b82f6", "#10b981", "#f97316", "#8b5cf6", "#ef4444", "#06b6d4"];

// Client accent colors — distinct palette per client slot
const CLIENT_ACCENTS = [
    "#f59e0b", "#3b82f6", "#10b981", "#8b5cf6",
    "#f97316", "#06b6d4", "#ec4899", "#ef4444",
];

const fmt = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

// ── SLD Symbol renderers ──────────────────────────────────────────────────────
function SLDMeterSymbol({ el, livePower }) {
    const w = el.width; const h = el.height;
    const screenH = h * 0.32; const screenY = h * 0.08;
    const arrowY1 = h * 0.78; const arrowY2 = h * 0.86;
    let displayPower = "465";
    if (livePower !== undefined && livePower !== null) {
        let powerValue = livePower;
        if (typeof livePower === 'string' && livePower.includes('kW')) powerValue = livePower.replace('kW', '').trim();
        const numValue = parseFloat(powerValue);
        displayPower = !isNaN(numValue) ? `${Math.round(numValue)}` : "0";
    }
    return (
        <g transform={`translate(${el.x},${el.y})`}>
            <rect width={w} height={h} rx={3} fill="rgba(0,0,0,0.18)" transform="translate(1.5,2)" />
            <rect width={w} height={h} rx={3} fill="#1e4d20" stroke="#134015" strokeWidth={1.2} />
            <rect x={w * 0.1} y={screenY} width={w * 0.8} height={screenH} rx={1.5} fill="#0f2e11" />
            <text x={w / 2} y={screenY + screenH * 0.62} textAnchor="middle" fontSize={Math.max(4, w * 0.18)} fontWeight="600" fill="#4ade80" fontFamily="monospace">{displayPower} kW</text>
            <line x1={w * 0.08} y1={h * 0.44} x2={w * 0.92} y2={h * 0.44} stroke="#134015" strokeWidth={0.75} />
            <text x={w / 2} y={screenY + screenH * 1.5} textAnchor="middle" fontSize={Math.max(3, w * 0.09)} fill="#86efac" fontFamily="monospace">SCHNEIDER</text>
            <rect x={w * 0.12} y={h * 0.62} width={w * 0.36} height={h * 0.1} rx={1} fill="#134015" />
            <rect x={w * 0.52} y={h * 0.62} width={w * 0.36} height={h * 0.1} rx={1} fill="#134015" />
            <polygon points={`${w * 0.30},${arrowY1} ${w * 0.18},${arrowY2} ${w * 0.42},${arrowY2}`} fill="#ef4444" />
            <polygon points={`${w * 0.70},${arrowY2} ${w * 0.58},${arrowY1} ${w * 0.82},${arrowY1}`} fill="#ef4444" />
            <line x1={w * 0.1} y1={h * 0.92} x2={w * 0.9} y2={h * 0.92} stroke="#134015" strokeWidth={0.75} />
        </g>
    );
}
function SLDBreakerSymbol({ el }) {
    const w = el.width; const h = el.height;
    return (<g transform={`translate(${el.x},${el.y})`}><rect width={w} height={h} rx={2} fill="#f8fafc" stroke="#64748b" strokeWidth={1.5} /><line x1={w * 0.18} y1={h * 0.2} x2={w * 0.82} y2={h * 0.8} stroke="#475569" strokeWidth={1.3} /><line x1={w * 0.82} y1={h * 0.2} x2={w * 0.18} y2={h * 0.8} stroke="#475569" strokeWidth={1.3} /></g>);
}
function SLDTransformerSymbol({ el }) {
    const w = el.width; const h = el.height; const r = Math.min(w, h) * 0.28; const cx = w / 2;
    return (<g transform={`translate(${el.x},${el.y})`}><circle cx={cx} cy={h * 0.3} r={r} fill="#f8fafc" stroke="#64748b" strokeWidth={1.5} /><circle cx={cx} cy={h * 0.7} r={r} fill="#f8fafc" stroke="#64748b" strokeWidth={1.5} /><line x1={cx - r} y1={h / 2} x2={cx + r} y2={h / 2} stroke="#94a3b8" strokeWidth={0.75} strokeDasharray="2 2" /></g>);
}
function SLDDisconnectSymbol({ el }) {
    const w = el.width; const h = el.height;
    return (<g transform={`translate(${el.x},${el.y})`}><circle cx={w / 2} cy={h * 0.2} r={4} fill="#f8fafc" stroke="#64748b" strokeWidth={1.5} /><circle cx={w / 2} cy={h * 0.8} r={4} fill="#f8fafc" stroke="#64748b" strokeWidth={1.5} /><line x1={w / 2} y1={h * 0.2} x2={w * 0.75} y2={h * 0.65} stroke="#64748b" strokeWidth={1.5} strokeLinecap="round" /></g>);
}
function SLDGatewaySymbol({ el }) {
    const w = el.width; const h = el.height; const cx = w / 2; const cy = h / 2;
    return (<g transform={`translate(${el.x},${el.y})`}><rect width={w} height={h} rx={3} fill="#f8fafc" stroke="#1d4ed8" strokeWidth={1.5} /><path d={`M ${cx - 14} ${cy - 2} Q ${cx} ${cy - 12} ${cx + 14} ${cy - 2}`} fill="none" stroke="#1d4ed8" strokeWidth={1.3} strokeLinecap="round" /><path d={`M ${cx - 8} ${cy + 4} Q ${cx} ${cy - 2} ${cx + 8} ${cy + 4}`} fill="none" stroke="#1d4ed8" strokeWidth={1.3} strokeLinecap="round" /><circle cx={cx} cy={cy + 9} r={2.5} fill="#1d4ed8" /></g>);
}
function SLDLoadSymbol({ el }) {
    const w = el.width; const h = el.height;
    return (<g transform={`translate(${el.x},${el.y})`}><rect width={w} height={h} rx={2} fill="white" stroke="#94a3b8" strokeWidth={1} /><text x={w / 2} y={h / 2 + 4} textAnchor="middle" fontSize={Math.min(11, h * 0.38)} fill="#374151" fontFamily="'Segoe UI', Arial, sans-serif">{el.label}</text></g>);
}
function SLDBusBarSymbol({ el }) {
    const w = el.width; const h = el.height; const mid = h / 2;
    return (<g transform={`translate(${el.x},${el.y})`}><line x1={0} y1={mid} x2={w} y2={mid} stroke={el.color || '#e07820'} strokeWidth={Math.max(h * 0.55, 4)} strokeLinecap="round" /><text x={-6} y={mid + 4} textAnchor="end" fontSize={9} fill={el.color || '#e07820'} fontFamily="'Segoe UI', Arial, sans-serif" fontWeight="600">{el.label}</text></g>);
}
function SLDTextLabelSymbol({ el }) {
    const w = el.width; const h = el.height; const fontSize = el.fontSize || 13;
    return (<g transform={`translate(${el.x},${el.y})`}>{el.showBorder && <rect width={w} height={h} rx={2} fill="rgba(255,255,255,0.8)" stroke="#94a3b8" strokeWidth={1} />}<foreignObject x={4} y={2} width={w - 8} height={h - 4} style={{ overflow: 'visible' }}><div xmlns="http://www.w3.org/1999/xhtml" style={{ fontSize: `${fontSize}px`, fontWeight: el.bold ? '700' : '400', fontStyle: el.italic ? 'italic' : 'normal', color: el.textColor || '#1f2937', fontFamily: "'Segoe UI', Arial, sans-serif", lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word', userSelect: 'none', pointerEvents: 'none' }}>{el.label}</div></foreignObject></g>);
}

// ── Full SLD preview ──────────────────────────────────────────────────────────
function DiagramPreview({ elements = [], freeLines = [], meterDataMap = {}, onMeterClick }) {
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const containerRef = useRef(null);
    const isPanning = useRef(false);
    const lastPos = useRef({ x: 0, y: 0 });

    if (elements.length === 0) {
        return (
            <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
                <ElectricBoltIcon sx={{ fontSize: 52, color: "#94a3b8", opacity: 0.35 }} />
                <Typography fontSize={13} color="text.secondary" fontWeight={500}>Empty diagram — open the editor to start</Typography>
            </Box>
        );
    }

    const allX = [...elements.map(e => e.x), ...elements.map(e => e.x + (e.width || 60)), ...freeLines.flatMap(l => l.points?.map(p => p.x) || [])];
    const allY = [...elements.map(e => e.y), ...elements.map(e => e.y + (e.height || 60)), ...freeLines.flatMap(l => l.points?.map(p => p.y) || [])];
    const pad = 50;
    const minX = Math.min(...allX) - pad; const minY = Math.min(...allY) - pad;
    const vw = Math.max(...allX) + pad - minX; const vh = Math.max(...allY) + pad - minY;

    const handleWheel = (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setScale(s => Math.min(Math.max(s * delta, 0.2), 5));
    };

    const handleMouseDown = (e) => {
        if (e.button === 1 || e.button === 0) {
            isPanning.current = true;
            lastPos.current = { x: e.clientX, y: e.clientY };
        }
    };
    const handleMouseMove = (e) => {
        if (!isPanning.current) return;
        const dx = e.clientX - lastPos.current.x;
        const dy = e.clientY - lastPos.current.y;
        setOffset(o => ({ x: o.x + dx, y: o.y + dy }));
        lastPos.current = { x: e.clientX, y: e.clientY };
    };
    const handleMouseUp = () => { isPanning.current = false; };

    const getLiveLabel = (el) => {
        if (el.type !== 'Meter' || !el.assignedMeter) return el.label;
        const live = meterDataMap[el.assignedMeter];
        if (!live?.data?.[0]) return el.label;
        const kw = live.data[0].total_active_power_kw;
        return kw !== null && kw !== undefined ? `${Math.round(kw)}` : el.label;
    };

    return (
        <Box
            ref={containerRef}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            sx={{ width: '100%', height: '100%', overflow: 'hidden', cursor: isPanning.current ? 'grabbing' : 'grab', userSelect: 'none', position: 'relative' }}
        >
            {/* Zoom controls */}
            <Box sx={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 10, display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'rgba(22,27,34,0.9)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, px: 1, py: 0.5, backdropFilter: 'blur(8px)' }}>
                <IconButton size="small" onClick={() => setScale(s => Math.max(s * 0.8, 0.2))} sx={{ color: '#94a3b8', '&:hover': { color: '#e2e8f0' } }}>
                    <Typography fontSize={18} lineHeight={1} sx={{ mb: 0.25 }}>−</Typography>
                </IconButton>
                <Typography fontSize={11} color="#64748b" fontFamily="monospace" sx={{ minWidth: 40, textAlign: 'center' }}>{Math.round(scale * 100)}%</Typography>
                <IconButton size="small" onClick={() => setScale(s => Math.min(s * 1.25, 5))} sx={{ color: '#94a3b8', '&:hover': { color: '#e2e8f0' } }}>
                    <Typography fontSize={18} lineHeight={1} sx={{ mb: 0.25 }}>+</Typography>
                </IconButton>
                <Box sx={{ width: '1px', height: 16, bgcolor: 'rgba(255,255,255,0.1)', mx: 0.5 }} />
                <IconButton size="small" onClick={() => { setScale(1); setOffset({ x: 0, y: 0 }); }} sx={{ color: '#94a3b8', '&:hover': { color: '#e2e8f0' } }}>
                    <Typography fontSize={10} fontFamily="monospace">FIT</Typography>
                </IconButton>
            </Box>

            {/* Centered, zoomable SVG */}
            <Box sx={{
                width: '100%', height: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <Box sx={{
                    transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                    transformOrigin: 'center center',
                    transition: isPanning.current ? 'none' : 'transform 0.05s',
                }}>
                    <svg viewBox={`${minX} ${minY} ${vw} ${vh}`} width={vw} height={vh} style={{ display: "block" }}>
                        {freeLines.map(line => {
                            if (!line.points || line.points.length < 2) return null;
                            const [s, e] = line.points;
                            return (<g key={line.id}><line x1={s.x} y1={s.y} x2={e.x} y2={e.y} stroke={line.color || "#374151"} strokeWidth={line.width || 1.5} strokeDasharray={line.style === "dashed" ? "6,4" : undefined} />{line.startAnchor && <circle cx={s.x} cy={s.y} r={3} fill={line.color || "#374151"} />}{line.endAnchor && <circle cx={e.x} cy={e.y} r={3} fill={line.color || "#374151"} />}</g>);
                        })}
                        {elements.map(el => {
                            const hasLive = el.type === 'Meter' && el.assignedMeter && !!meterDataMap[el.assignedMeter];
                            const liveLabel = getLiveLabel(el);
                            const isClickable = el.type === 'Meter' && el.assignedMeter && hasLive;
                            return (
                                <g key={el.id} onClick={() => isClickable && onMeterClick?.(el, meterDataMap[el.assignedMeter])} style={{ cursor: isClickable ? 'pointer' : 'default' }}>
                                    {el.type === 'Meter' && <SLDMeterSymbol el={el} livePower={liveLabel} />}
                                    {el.type === 'Gateway' && <SLDGatewaySymbol el={el} />}
                                    {el.type === 'Power_Button' && <SLDBreakerSymbol el={el} />}
                                    {el.type === 'Gateway2' && <SLDTransformerSymbol el={el} />}
                                    {el.type === 'CIRCUIT_BREAKER' && <SLDDisconnectSymbol el={el} />}
                                    {el.type === 'Load' && <SLDLoadSymbol el={el} />}
                                    {el.type === 'BusBar' && <SLDBusBarSymbol el={el} />}
                                    {el.type === 'TextLabel' && <SLDTextLabelSymbol el={el} />}
                                </g>
                            );
                        })}
                    </svg>
                </Box>
            </Box>
        </Box>
    );
}

// ── Meter Detail Popup ────────────────────────────────────────────────────────
function MeterDetailPopup({ open, onClose, element, liveData }) {
    if (!element || !liveData) return null;
    const data = liveData.data?.[0];
    const metrics = [
        { label: "Recorded At", value: data?.recorded_at ? new Date(data.recorded_at).toLocaleString() : "—", unit: "", hi: false },
        { label: "Active Power", value: data?.total_active_power_kw != null ? data.total_active_power_kw.toFixed(2) : "—", unit: "kW", hi: true },
        { label: "Apparent Power", value: data?.total_apparent_power_kva != null ? data.total_apparent_power_kva.toFixed(2) : "—", unit: "kVA", hi: false },
        { label: "Power Factor", value: data?.total_pf != null ? data.total_pf.toFixed(3) : "—", unit: "", hi: false },
        { label: "Voltage Unbalance", value: data?.voltage_unbalance_percent != null ? data.voltage_unbalance_percent.toFixed(2) : "—", unit: "%", hi: false },
        { label: "Current Unbalance", value: data?.current_unbalance_percent != null ? data.current_unbalance_percent.toFixed(2) : "—", unit: "%", hi: false },
    ];
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
            <Box sx={{ bgcolor: '#1e4d20', px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <svg width="28" height="36" viewBox="0 0 28 36"><rect x="1" y="1" width="26" height="34" rx="2" fill="#1a4518" stroke="#134015" strokeWidth="1" /><rect x="3" y="3" width="22" height="10" rx="1" fill="#0f2e11" /><polygon points="8,26 5,21 11,21" fill="#ef4444" /><polygon points="20,21 17,26 23,26" fill="#ef4444" /></svg>
                    <Box>
                        <Typography fontWeight={700} fontSize={15} color="white">{element.name}</Typography>
                        <Typography fontSize={11} color="rgba(255,255,255,0.65)">SN: {element.assignedMeter}</Typography>
                    </Box>
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)' }}><CloseIcon fontSize="small" /></IconButton>
            </Box>
            <Box sx={{ bgcolor: '#f0fdf4', px: 2.5, py: 0.75, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #dcfce7' }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#22c55e' }} />
                <Typography fontSize={11} color="#166534" fontWeight={700} letterSpacing="0.06em">LIVE DATA</Typography>
            </Box>
            <DialogContent sx={{ p: 0 }}>
                <List disablePadding>
                    {metrics.map((m, i) => (
                        <ListItem key={i} sx={{ px: 2.5, py: 1.25, borderBottom: i < metrics.length - 1 ? '1px solid #f1f5f9' : 'none', bgcolor: m.hi ? 'rgba(34,134,58,0.05)' : 'white' }}>
                            <ListItemText primary={<Typography fontSize={12} color="#64748b">{m.label}</Typography>} />
                            <Typography fontSize={m.hi ? 20 : 14} fontWeight={m.hi ? 800 : 500} color={m.hi ? '#1e4d20' : '#1e293b'}>{m.value}{m.unit && m.value !== "—" ? ` ${m.unit}` : ""}</Typography>
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #f1f5f9' }}>
                <Button onClick={onClose} size="small" sx={{ textTransform: "none" }}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Right Sidebar ─────────────────────────────────────────────────────────────
function RightSidebar({ meterDataMap, elements, loading, onRefresh, lastRefreshTime }) {
    const assignedMeters = elements?.filter(el => el.type === 'Meter' && el.assignedMeter) || [];
    const meterDetails = assignedMeters.map(meter => {
        const live = meterDataMap[meter.assignedMeter];
        // Get raw power - could be 0, negative, or undefined
        const rawPower = live?.data?.[0]?.total_active_power_kw;
        // Check if power value actually exists (not undefined/null)
        const hasValidPower = rawPower !== null && rawPower !== undefined;
        return {
            id: meter.id,
            serialNumber: meter.assignedMeter,
            name: live?.description || meter.name,
            power: hasValidPower ? Math.round(rawPower) : 0,
            rawPower: hasValidPower ? rawPower : null,
            pf: live?.data?.[0]?.total_pf || 0,
            hasData: !!live  // Has API data (even if power is 0 or negative)
        };
    });

    // Total power - only sum if value exists (ignore null)
    const totalPower = meterDetails.reduce((sum, m) => {
        if (m.rawPower !== null && m.rawPower !== undefined) {
            return sum + m.rawPower;
        }
        return sum;
    }, 0);

    // Active meters: has data AND power value exists (even if 0 or negative)
    const activeMeters = meterDetails.filter(m => m.hasData && m.rawPower !== null && m.rawPower !== undefined).length;
    const avgPower = activeMeters > 0 ? totalPower / activeMeters : 0;

    return (
        <Paper elevation={0} sx={{ width: 300, height: '100%', bgcolor: '#1a1f2e', borderLeft: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ p: 1.5, borderBottom: '1px solid rgba(255,255,255,0.06)', bgcolor: '#1e2330' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <Typography fontWeight={700} fontSize={13} color="#e2e8f0">Meter Dashboard</Typography>
                    <Tooltip title="Refresh data"><IconButton size="small" onClick={onRefresh} disabled={loading} sx={{ color: '#64748b', '&:hover': { color: '#e2e8f0' } }}><RefreshIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                </Stack>
                {lastRefreshTime && <Typography fontSize={10} color="#475569" fontFamily="monospace">Updated {lastRefreshTime.toLocaleTimeString()}</Typography>}
            </Box>
            <Box sx={{ p: 1.5, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <Stack spacing={1}>
                    <Box sx={{ p: 1.5, bgcolor: 'rgba(34,134,58,0.1)', border: '1px solid rgba(34,134,58,0.2)', borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography fontSize={10} color="#4ade80" fontWeight={600} letterSpacing="0.06em">TOTAL POWER</Typography>
                            <Typography fontSize={22} fontWeight={800} color="#4ade80" fontFamily="monospace">{Math.round(totalPower)}<Typography component="span" fontSize={11} fontWeight={400} color="#86efac" sx={{ ml: 0.5 }}>kW</Typography></Typography>
                        </Box>
                        <BoltIcon sx={{ fontSize: 28, color: '#4ade80', opacity: 0.6 }} />
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Box sx={{ flex: 1, p: 1, bgcolor: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 1.5 }}>
                            <Typography fontSize={10} color="#60a5fa">Active</Typography>
                            <Typography fontSize={18} fontWeight={700} color="#60a5fa" fontFamily="monospace">{activeMeters}<Typography component="span" fontSize={11} color="#94a3b8">/{assignedMeters.length}</Typography></Typography>
                        </Box>
                        <Box sx={{ flex: 1, p: 1, bgcolor: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 1.5 }}>
                            <Typography fontSize={10} color="#f59e0b">Avg / Meter</Typography>
                            <Typography fontSize={18} fontWeight={700} color="#f59e0b" fontFamily="monospace">{avgPower.toFixed(1)}<Typography component="span" fontSize={10} color="#94a3b8"> kW</Typography></Typography>
                        </Box>
                    </Stack>
                </Stack>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', p: 1, '&::-webkit-scrollbar': { width: 3 }, '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.1)', borderRadius: 2 } }}>
                <Typography fontSize={9} fontWeight={700} color="#334155" letterSpacing="0.1em" sx={{ px: 1, mb: 1, textTransform: 'uppercase' }}>Assigned Meters ({meterDetails.length})</Typography>
                {loading && meterDetails.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} sx={{ color: '#475569' }} /></Box>
                ) : meterDetails.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}><DevicesIcon sx={{ fontSize: 30, color: '#2d3748', mb: 1 }} /><Typography fontSize={12} color="#475569">No meters assigned</Typography></Box>
                ) : (
                    <Stack spacing={0.75}>
                        {meterDetails.map((meter) => {
                            // Check if meter is online: has data AND power value exists (even if 0 or negative)
                            const isOnline = meter.hasData && meter.rawPower !== null && meter.rawPower !== undefined;
                            const statusColor = isOnline ? '#22c55e' : '#ef4444';
                            const statusText = isOnline ? 'Online' : 'Offline';

                            // Format power display
                            const displayPower = isOnline ? meter.rawPower.toFixed(1) : '—';
                            const isGenerating = isOnline && meter.rawPower < 0;

                            return (
                                <Box key={meter.id} sx={{
                                    p: 1.25,
                                    borderRadius: 1.5,
                                    bgcolor: isOnline ? 'rgba(255,255,255,0.03)' : 'rgba(239,68,68,0.05)',
                                    border: `1px solid ${isOnline ? 'rgba(255,255,255,0.06)' : 'rgba(239,68,68,0.15)'}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.25,
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                                    transition: 'background .15s'
                                }}>
                                    {/* Status Dot */}
                                    <Tooltip title={statusText} arrow placement="left">
                                        <Box sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            bgcolor: statusColor,
                                            boxShadow: isOnline ? '0 0 0 2px rgba(34,197,94,0.2)' : 'none',
                                            flexShrink: 0,
                                            animation: isOnline ? 'pulse 2s infinite' : 'none',
                                            '@keyframes pulse': {
                                                '0%': { boxShadow: '0 0 0 0 rgba(34,197,94,0.4)' },
                                                '70%': { boxShadow: '0 0 0 4px rgba(34,197,94,0)' },
                                                '100%': { boxShadow: '0 0 0 0 rgba(34,197,94,0)' },
                                            }
                                        }} />
                                    </Tooltip>

                                    <Box sx={{ flexShrink: 0 }}><svg width="18" height="24" viewBox="0 0 18 24"><rect x="0.5" y="0.5" width="17" height="23" rx="2" fill="#1e4d20" stroke="#134015" strokeWidth="0.8" /><rect x="2" y="2" width="14" height="7" rx="1" fill="#0f2e11" /><text x="9" y="7" textAnchor="middle" fontSize="3.5" fill="#4ade80" fontFamily="monospace">PM</text><polygon points="5,18 3,15 7,15" fill="#ef4444" /><polygon points="13,15 11,18 15,18" fill="#ef4444" /></svg></Box>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                                            <Typography fontSize={11} fontWeight={600} color="#cbd5e1" noWrap>{meter.name}</Typography>
                                            <Typography fontSize={9} color={statusColor} fontWeight={500}>{statusText}</Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                                            <Typography fontSize={12} fontWeight={700} color={isOnline ? '#4ade80' : '#ef4444'} fontFamily="monospace">
                                                {displayPower} kW
                                            </Typography>
                                            {isOnline && meter.pf > 0 && <Typography fontSize={10} color="#475569">PF: {meter.pf.toFixed(2)}</Typography>}

                                        </Stack>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </Box>
            <Box sx={{ p: 1.25, borderTop: '1px solid rgba(255,255,255,0.06)', bgcolor: '#1e2330' }}>
                <Typography fontSize={10} color="#334155" textAlign="center" fontFamily="monospace">Auto-refresh every 5 minutes</Typography>
            </Box>
        </Paper>
    );
}

// ── Main Gallery ──────────────────────────────────────────────────────────────
export default function DiagramGallery() {
    const navigate = useNavigate();

    // ── User & clients state ──
    const [userDetail, setUserDetail] = useState(null);
    const [loadingUser, setLoadingUser] = useState(true);
    const [clientList, setClientList] = useState([]);
    const [loadingClients, setLoadingClients] = useState(false);

    // ── Selected client ──
    const [selectedClientId, setSelectedClientId] = useState(null);

    // ── Diagrams per client (cache: { clientId: [diagrams] }) ──
    const [diagramsCache, setDiagramsCache] = useState({});
    const [loadingDiagrams, setLoadingDiagrams] = useState(false);

    // ── Selected diagram within current client ──
    const [selectedDiagramId, setSelectedDiagramId] = useState(null);

    // ── Meter data ──
    const [meterDataMap, setMeterDataMap] = useState({});
    const [loadingMeters, setLoadingMeters] = useState(false);
    const [lastRefreshTime, setLastRefreshTime] = useState(null);

    // ── UI state ──
    const [showSidebar, setShowSidebar] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [meterPopup, setMeterPopup] = useState(null);

    // ── Dialogs ──
    const [createOpen, setCreateOpen] = useState(false);
    const [newName, setNewName] = useState("");
    const [creating, setCreating] = useState(false);
    const [renameOpen, setRenameOpen] = useState(false);
    const [renameDiagram, setRenameDiagram] = useState(null);
    const [renameName, setRenameName] = useState("");
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuDiagram, setMenuDiagram] = useState(null);
    const [createClientId, setCreateClientId] = useState(null);

    const showSnack = (message, severity = "success") => setSnackbar({ open: true, message, severity });

    // ── Current diagrams list (for selected client) ──
    const currentDiagrams = (selectedClientId && diagramsCache[selectedClientId]) || [];
    const visibleClients = clientList.filter(client => {
        const clientId = String(client.client_id);
        const diagrams = diagramsCache[clientId];
        // Only show if diagrams exist AND have at least one diagram
        return diagrams && diagrams.length > 0;
    });

    // ── Load user ──
    useEffect(() => {
        const fetchUser = async () => {
            try {
                setLoadingUser(true);
                const userData = await getUserDetails();
                setUserDetail(userData);
                if (userData?.client_id) {
                    localStorage.setItem("client_id", String(userData.client_id));
                }
            } catch {
                showSnack("Failed to load user configuration", "error");
            } finally {
                setLoadingUser(false);
            }
        };
        fetchUser();
    }, []);

    // ── Load client list & auto-select first ──
    useEffect(() => {
        const fetchClients = async () => {
            setLoadingClients(true);
            try {
                const plantList = await getPlantList();
                if (Array.isArray(plantList.data)) {
                    const filtered = plantList.data.filter(
                        c => c.access_level === 1 && (c.user_type === 0 || c.user_type === 1)
                    );
                    setClientList(filtered);
                    // Auto-select first client
                    if (filtered.length > 0) {
                        setSelectedClientId(filtered[0].client_id);
                    }
                }
            } catch {
                showSnack("Failed to load clients", "error");
            } finally {
                setLoadingClients(false);
            }
        };
        fetchClients();
    }, []);

    // ── Load diagrams for a client (with cache) ──
    const loadDiagramsForClient = useCallback(async (clientId) => {
        if (!clientId) return;
        // If already cached, don't refetch
        if (diagramsCache[clientId]) return;

        setLoadingDiagrams(true);
        try {
            const token = sessionStorage.getItem('accessToken');
            const listRes = await fetch(`${API_BASE}/api/diagram/?client_id=${clientId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const listData = await listRes.json();

            // ✅ If no diagrams or API returns empty, store empty array
            if (!listData.success || !listData.diagrams || listData.diagrams.length === 0) {
                setDiagramsCache(prev => ({ ...prev, [clientId]: [] }));
                setLoadingDiagrams(false);
                return;
            }

            let diagramIds = [];
            if (listData.diagrams) diagramIds = listData.diagrams.map(d => d.diagram_id || d.id);
            else if (Array.isArray(listData.data)) diagramIds = listData.data.map(d => d.diagram_id || d.id);

            const diagramsList = await Promise.all(diagramIds.map(async (id) => {
                const token = sessionStorage.getItem('accessToken');
                const detailRes = await fetch(
                    `${API_BASE}/api/diagram/?client_id=${clientId}&diagram_id=${id}`,
                    { headers: { Authorization: `Bearer ${token}` }, }
                );
                const detailData = await detailRes.json();
                if (detailData.success && detailData.data) {
                    const d = detailData.data;
                    return {
                        diagram_id: String(id),
                        name: d.name,
                        elements: d.elements || [],
                        freeLines: d.freeLines || [],
                        version: d.version || 1,
                        zoom: d.zoom || 1,
                        panOffset: d.panOffset || { x: 0, y: 0 },
                        meter_data_client_id: d.meter_data_client_id || clientId,
                        created_at: d.created_at,
                        updated_at: d.updated_at,
                        owner_client_id: clientId,
                    };
                }
                return null;
            }));

            const valid = diagramsList.filter(Boolean);
            setDiagramsCache(prev => ({ ...prev, [clientId]: valid }));

            // Auto-select first client that has diagrams
            if (!selectedClientId && valid.length > 0) {
                setSelectedClientId(clientId);
                setSelectedDiagramId(valid[0].diagram_id);
            }
        } catch (error) {
            console.error("Failed to load diagrams", error);
            setDiagramsCache(prev => ({ ...prev, [clientId]: [] }));
        } finally {
            setLoadingDiagrams(false);
        }
    }, [diagramsCache, selectedClientId]);

    // ── Load ALL clients' diagrams on initial load (REPLACES the old single-client load) ──
    useEffect(() => {
        const loadAllClientsDiagrams = async () => {
            if (!clientList.length) return;

            // Load diagrams for ALL clients in parallel
            const loadPromises = clientList.map(client =>
                loadDiagramsForClient(String(client.client_id))
            );

            await Promise.all(loadPromises);
        };

        loadAllClientsDiagrams();
    }, [clientList]); // Run when clientList changes

    // ── When selected client changes, update selected diagram ──
    useEffect(() => {
        if (!selectedClientId) return;
        const diagrams = diagramsCache[selectedClientId];
        if (diagrams && diagrams.length > 0) {
            // If current selected diagram is not in this client, select first one
            if (!selectedDiagramId || !diagrams.find(d => d.diagram_id === selectedDiagramId)) {
                setSelectedDiagramId(diagrams[0].diagram_id);
            }
        } else {
            setSelectedDiagramId(null);
        }
        // Clear meter data when switching clients
        setMeterDataMap({});
    }, [selectedClientId, diagramsCache]);

    // ── Fetch meter data for selected diagram ──
    const fetchMeterDataForDiagram = useCallback(async (diagram) => {
        if (!diagram) return {};
        const neededSerials = new Set((diagram.elements || []).map(el => el.assignedMeter).filter(Boolean));
        if (neededSerials.size === 0) return {};
        const elementClientIds = [...new Set((diagram.elements || []).map(el => el.meterClientId).filter(Boolean))];
        const clientIds = elementClientIds.length > 0 ? elementClientIds : [diagram.meter_data_client_id].filter(Boolean);
        const results = await Promise.allSettled(clientIds.map(id => diagram_meter_data(parseInt(id))));
        const dataMap = {};
        results.forEach(result => {
            if (result.status !== "fulfilled" || !result.value) return;
            const response = result.value;
            let meterArray = Array.isArray(response) ? response : (Array.isArray(response?.data) ? response.data : []);
            meterArray.forEach(meter => {
                if (meter.serial_number && neededSerials.has(meter.serial_number)) dataMap[meter.serial_number] = meter;
            });
        });
        return dataMap;
    }, []);

    const refreshMeterData = useCallback(async () => {
        const selected = currentDiagrams.find(d => d.diagram_id === selectedDiagramId);
        if (!selected) return;
        setLoadingMeters(true);
        try {
            const dataMap = await fetchMeterDataForDiagram(selected);
            setMeterDataMap(dataMap);
            setLastRefreshTime(new Date());
            const count = Object.keys(dataMap).length;
            if (count > 0) showSnack(`${count} meter(s) loaded`, "success");
        } catch {
            showSnack("Failed to refresh meter data", "error");
        } finally {
            setLoadingMeters(false);
        }
    }, [selectedDiagramId, currentDiagrams, fetchMeterDataForDiagram]);

    const refreshMeterDataRef = useRef(null);
    refreshMeterDataRef.current = refreshMeterData;

    useEffect(() => {
        if (!selectedDiagramId) return;
        setMeterDataMap({});
        refreshMeterDataRef.current();
        const interval = setInterval(() => refreshMeterDataRef.current(), 300000);
        return () => clearInterval(interval);
    }, [selectedDiagramId]);

    // ── Helpers ──
    const invalidateCacheForClient = (clientId) => {
        setDiagramsCache(prev => { const next = { ...prev }; delete next[clientId]; return next; });
    };

    const handleOpen = (diagramId) => {
        const diagram = currentDiagrams.find(d => d.diagram_id === diagramId);
        if (!diagram) return;

        localStorage.setItem("lastDiagramId", diagramId);
        localStorage.setItem("diagram_owner_client_id", String(diagram.owner_client_id));
        localStorage.setItem("diagram_client_name", clientList.find(c => c.client_id === diagram.owner_client_id)?.company_name || "");
        localStorage.setItem("editingDiagramName", diagram.name);
        localStorage.removeItem("pendingDiagramName");

        navigate("/DiagramEditor");
    };

    const handleCreate = () => {
        if (!newName.trim() || !createClientId) {
            showSnack("Please select a client and enter a diagram name", "error");
            return;
        }

        setCreating(true);

        // Store diagram info for the editor
        localStorage.setItem("pendingDiagramName", newName.trim());
        localStorage.setItem("diagram_owner_client_id", String(createClientId));
        localStorage.setItem("diagram_client_name", clientList.find(c => c.client_id === createClientId)?.company_name || "");
        localStorage.removeItem("lastDiagramId");
        localStorage.removeItem("editingDiagramId");

        setCreateOpen(false);
        setNewName("");
        setCreateClientId(null);
        setCreating(false);

        // Navigate to editor
        navigate("/DiagramEditor");
    };

    const handleRename = async () => {
        if (!renameName.trim() || !renameDiagram) return;
        try {
            const ownerClientId = renameDiagram.owner_client_id || selectedClientId;
            const token = sessionStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE}/api/diagram/`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    client_id: parseInt(ownerClientId), diagramId: renameDiagram.diagram_id,
                    name: renameName.trim(), elements: renameDiagram.elements || [],
                    freeLines: renameDiagram.freeLines || [], zoom: renameDiagram.zoom || 1,
                    panOffset: renameDiagram.panOffset || { x: 0, y: 0 }, version: renameDiagram.version || 1,
                    meter_data_client_id: renameDiagram.meter_data_client_id || ownerClientId,
                }),
            });
            const data = await res.json();
            if (data.success) {
                showSnack("Renamed successfully");
                setRenameOpen(false); setRenameDiagram(null);
                invalidateCacheForClient(ownerClientId);
                loadDiagramsForClient(ownerClientId);
            } else throw new Error(data.error || "Rename failed");
        } catch (err) { showSnack("Failed to rename: " + err.message, "error"); }
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;
        try {
            const ownerClientId = deleteConfirm.ownerClientId || selectedClientId;
            const token = sessionStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE}/api/diagram/`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ client_id: parseInt(ownerClientId), diagram_id: parseInt(deleteConfirm.id), _method: "DELETE" }),
            });
            const data = await res.json();
            if (data.success) {
                showSnack(`"${deleteConfirm.name}" deleted`, "info");
                setDeleteConfirm(null);
                invalidateCacheForClient(ownerClientId);
                // Update cache optimistically
                setDiagramsCache(prev => ({
                    ...prev,
                    [ownerClientId]: (prev[ownerClientId] || []).filter(d => d.diagram_id !== deleteConfirm.id),
                }));
                const remaining = (diagramsCache[ownerClientId] || []).filter(d => d.diagram_id !== deleteConfirm.id);
                setSelectedDiagramId(remaining.length > 0 ? remaining[0].diagram_id : null);
            } else throw new Error(data.error || "Delete failed");
        } catch (err) { showSnack("Failed to delete: " + err.message, "error"); }
    };

    if (loadingUser) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#0d1117' }}>
                <CircularProgress size={36} sx={{ color: '#f59e0b' }} />
                <Typography sx={{ ml: 2, color: '#64748b', fontFamily: 'monospace', fontSize: 13 }}>Loading...</Typography>
            </Box>
        );
    }

    const selected = currentDiagrams.find(d => d.diagram_id === selectedDiagramId);
    const selectedDiagramIndex = currentDiagrams.findIndex(d => d.diagram_id === selectedDiagramId);
    const accent = ACCENTS[selectedDiagramIndex % ACCENTS.length] || "#3b82f6";
    const liveMeterCount = selected?.elements?.filter(el => el.type === 'Meter' && el.assignedMeter && meterDataMap[el.assignedMeter]).length || 0;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", bgcolor: "#0d1117" }}>

            {/* ══ TOP BAR ══ */}
            <Box sx={{
                bgcolor: "#161b22", borderBottom: "1px solid rgba(255,255,255,0.06)",
                px: 2.5, py: 1.25, flexShrink: 0,
                display: "flex", alignItems: "center", gap: 1.5,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                    <Typography fontWeight={800} fontSize={13} color="#e2e8f0" letterSpacing="0.05em">
                        Dynamic Diagram
                    </Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Button
                    variant="contained" disableElevation size="small"
                    startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                    onClick={() => { setNewName(""); setCreateOpen(true); }}
                    disabled={!selectedClientId}
                    sx={{ bgcolor: "#f59e0b", color: "#1a1a1a", borderRadius: 1.5, textTransform: "none", fontWeight: 700, fontSize: 12, px: 2, "&:hover": { bgcolor: "#d97706" } }}
                >
                    New Diagram
                </Button>
            </Box>

            {/* ══ CLIENT ROW ══ */}
            <Box sx={{
                bgcolor: "#0d1117",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                px: 1.5,
                py: 0,
                flexShrink: 0,
                display: "flex",
                alignItems: "stretch",
                overflowX: "auto",
                minHeight: 44,
                "&::-webkit-scrollbar": { height: 3 },
                "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(255,255,255,0.1)", borderRadius: 2 },
            }}>
                {/* "All Clients" prefix label */}
                <Box sx={{ display: 'flex', alignItems: 'center', pr: 1.5, mr: 1, borderRight: '1px solid rgba(255,255,255,0.07)' }}>
                    <BusinessIcon sx={{ fontSize: 13, color: '#334155', mr: 0.5 }} />
                    <Typography fontSize={10} fontWeight={700} color="#334155" letterSpacing="0.08em" textTransform="uppercase" noWrap>
                        Clients
                    </Typography>
                </Box>

                {loadingClients ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
                        <CircularProgress size={13} sx={{ color: '#475569' }} />
                    </Box>
                ) : (() => {
                    // Filter clients that have diagrams (cached and length > 0)
                    const clientsWithDiagrams = clientList.filter(client => {
                        const diagrams = diagramsCache[String(client.client_id)];
                        return diagrams && diagrams.length > 0;
                    });

                    if (clientsWithDiagrams.length === 0) {
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
                                <Typography fontSize={11} color="#334155" fontFamily="monospace">
                                    {Object.keys(diagramsCache).length === 0 ? "Loading diagrams..." : "Loading diagrams..."}
                                </Typography>
                            </Box>
                        );
                    }

                    return clientsWithDiagrams.map((client, i) => {
                        const clientAccent = CLIENT_ACCENTS[i % CLIENT_ACCENTS.length];
                        const isActive = selectedClientId === client.client_id;
                        const cachedDiagrams = diagramsCache[client.client_id];
                        const diagramCount = cachedDiagrams ? cachedDiagrams.length : 0;

                        return (
                            <Box
                                key={client.client_id}
                                onClick={() => setSelectedClientId(client.client_id)}
                                sx={{
                                    display: "flex", alignItems: "center", gap: 0.75,
                                    px: 2, cursor: "pointer", position: 'relative',
                                    borderBottom: isActive ? `2px solid ${clientAccent}` : "2px solid transparent",
                                    bgcolor: isActive ? `rgba(255,255,255,0.05)` : "transparent",
                                    transition: "all 0.15s",
                                    "&:hover": { bgcolor: "rgba(255,255,255,0.03)" },
                                    minHeight: 44,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {/* Color dot */}
                                <Box sx={{
                                    width: 7, height: 7, borderRadius: "50%",
                                    bgcolor: isActive ? clientAccent : 'rgba(255,255,255,0.15)',
                                    flexShrink: 0,
                                    transition: 'background 0.15s',
                                }} />

                                <Typography
                                    fontSize={12}
                                    fontWeight={isActive ? 700 : 400}
                                    color={isActive ? "#e2e8f0" : "#64748b"}
                                    sx={{ transition: 'color 0.15s' }}
                                >
                                    {client.company_name}
                                </Typography>

                                {/* Diagram count badge */}
                                <Box sx={{
                                    ml: 0.25,
                                    minWidth: 18, height: 16,
                                    borderRadius: '8px',
                                    bgcolor: isActive ? `${clientAccent}25` : 'rgba(255,255,255,0.06)',
                                    border: `1px solid ${isActive ? `${clientAccent}40` : 'rgba(255,255,255,0.08)'}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    px: 0.75,
                                    transition: 'all 0.15s',
                                }}>
                                    <Typography fontSize={9} fontWeight={700}
                                        color={isActive ? clientAccent : '#475569'}>
                                        {diagramCount}
                                    </Typography>
                                </Box>

                                {/* Loading spinner for selected client without cache */}
                                {isActive && loadingDiagrams && !cachedDiagrams && (
                                    <CircularProgress size={10} sx={{ color: clientAccent, ml: 0.5 }} />
                                )}
                            </Box>
                        );
                    });
                })()}
            </Box>

            {/* ══ DIAGRAM TABS (filtered by selected client) ══ */}
            <Box sx={{
                bgcolor: "#161b22", borderBottom: "1px solid rgba(255,255,255,0.06)",
                px: 1, flexShrink: 0,
                display: "flex", alignItems: "stretch",
                overflowX: "auto", minHeight: 40,
                "&::-webkit-scrollbar": { height: 3 },
                "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(255,255,255,0.1)", borderRadius: 2 },
            }}>
                {loadingDiagrams ? (
                    <Box sx={{ display: "flex", alignItems: "center", px: 2 }}>
                        <CircularProgress size={14} sx={{ color: '#475569' }} />
                        <Typography fontSize={11} color="#334155" sx={{ ml: 1, fontFamily: 'monospace' }}>Loading diagrams…</Typography>
                    </Box>
                ) : currentDiagrams.length === 0 ? (
                    <Box sx={{ display: "flex", alignItems: "center", px: 2 }}>
                        <Typography fontSize={12} color="#334155" fontFamily="monospace">
                            {selectedClientId ? "No diagrams for this client" : "Select a client above"}
                        </Typography>
                    </Box>
                ) : (
                    currentDiagrams.map((d, i) => {
                        const ac = ACCENTS[i % ACCENTS.length];
                        const isActive = selectedDiagramId === d.diagram_id;
                        return (
                            <Box key={d.diagram_id} onClick={() => setSelectedDiagramId(d.diagram_id)}
                                sx={{
                                    display: "flex", alignItems: "center", gap: 0.75,
                                    px: 2, cursor: "pointer",
                                    borderBottom: isActive ? `2px solid ${ac}` : "2px solid transparent",
                                    bgcolor: isActive ? `rgba(255,255,255,0.04)` : "transparent",
                                    transition: "all 0.15s",
                                    "&:hover": { bgcolor: "rgba(255,255,255,0.03)" },
                                    minHeight: 40,
                                }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: ac, flexShrink: 0 }} />
                                <Typography fontSize={12} fontWeight={isActive ? 700 : 400} color={isActive ? "#e2e8f0" : "#64748b"} noWrap>
                                    {d.name}
                                </Typography>
                                <IconButton size="small"
                                    onClick={e => { e.stopPropagation(); setMenuDiagram(d); setMenuAnchor(e.currentTarget); }}
                                    sx={{ p: 0.25, ml: 0.25, opacity: 0, color: '#64748b', transition: "opacity 0.15s", '&:hover': { opacity: 1, color: '#e2e8f0' }, '.MuiBox-root:hover &': { opacity: 0.4 } }}>
                                    <MoreVertIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                            </Box>
                        );
                    })
                )}
            </Box>

            {/* Tab context menu */}
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}
                PaperProps={{ sx: { bgcolor: '#1e2330', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 1.5, minWidth: 140 } }}>
                <MenuItem onClick={() => { setMenuAnchor(null); setRenameDiagram(menuDiagram); setRenameName(menuDiagram?.name || ""); setRenameOpen(true); }} sx={{ fontSize: 12, color: '#cbd5e1', gap: 1 }}>
                    <EditIcon sx={{ fontSize: 14 }} /> Rename
                </MenuItem>
                <MenuItem sx={{ fontSize: 12, color: '#f87171', gap: 1 }}
                    onClick={() => { setMenuAnchor(null); setDeleteConfirm({ id: menuDiagram?.diagram_id, name: menuDiagram?.name, ownerClientId: menuDiagram?.owner_client_id || selectedClientId }); }}>
                    <DeleteIcon sx={{ fontSize: 14 }} /> Delete
                </MenuItem>
            </Menu>

            {/* ══ PREVIEW SUB-HEADER ══ */}
            {selected && (
                <Box sx={{ bgcolor: "#1a1f2e", borderBottom: "1px solid rgba(255,255,255,0.06)", px: 2.5, py: 1, flexShrink: 0, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                    <Typography fontWeight={700} fontSize={13} color="#e2e8f0">{selected.name}</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 11, color: "#475569" }} />
                        <Typography fontSize={11} color="#475569" fontFamily="monospace">{fmt(selected.updated_at ?? selected.created_at)}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Chip label={`${(selected.elements || []).length} nodes`} size="small" sx={{ fontSize: 10, height: 20, bgcolor: 'rgba(255,255,255,0.05)', color: '#64748b', border: '1px solid rgba(255,255,255,0.07)' }} />
                    <Chip label={`${(selected.freeLines || []).length} wires`} size="small" sx={{ fontSize: 10, height: 20, bgcolor: 'rgba(255,255,255,0.05)', color: '#64748b', border: '1px solid rgba(255,255,255,0.07)' }} />
                    {liveMeterCount > 0 && <Chip icon={<BoltIcon sx={{ fontSize: 11 }} />} label={`${liveMeterCount} live`} size="small" sx={{ fontSize: 10, height: 20, bgcolor: 'rgba(34,197,94,0.15)', color: '#de4a4a', border: '1px solid rgba(34,197,94,0.25)' }} />}
                    <Chip label={`v${selected.version || 1}`} size="small" sx={{ fontSize: 10, height: 20, bgcolor: `rgba(245,158,11,0.12)`, color: '#f59e0b', border: '1px solid rgba(245,158,11,0.2)', fontWeight: 700 }} />
                    <Tooltip title="Toggle Meter Dashboard">
                        <IconButton size="small" onClick={() => setShowSidebar(!showSidebar)} sx={{ color: showSidebar ? '#f59e0b' : '#475569', bgcolor: showSidebar ? 'rgba(245,158,11,0.1)' : 'transparent', borderRadius: 1 }}>
                            <ShowChartIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </Tooltip>
                    <Button variant="contained" disableElevation size="small" startIcon={<EditIcon sx={{ fontSize: 13 }} />} onClick={() => handleOpen(selected.diagram_id)}
                        sx={{ bgcolor: '#f59e0b', color: '#1a1a1a', borderRadius: 1.5, textTransform: "none", fontWeight: 700, fontSize: 11, px: 1.5, "&:hover": { bgcolor: "#d97706" } }}>
                        Open Editor
                    </Button>
                </Box>
            )}

            {/* ══ MAIN CONTENT ══ */}
            <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
                <Box sx={{
                    flex: 1, overflow: "hidden", position: "relative", bgcolor: "#f4f5f7",
                    backgroundImage: "linear-gradient(rgba(0,0,0,0.06) 1px,transparent 1px),linear-gradient(90deg,rgba(0,0,0,0.06) 1px,transparent 1px)",
                    backgroundSize: "30px 30px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    {loadingDiagrams ? (
                        <CircularProgress sx={{ color: '#94a3b8' }} />
                    ) : selected ? (
                        <>
                            <Box sx={{ width: '100%', height: '100%' }}>
                                <DiagramPreview
                                    elements={selected.elements || []}
                                    freeLines={selected.freeLines || []}
                                    meterDataMap={meterDataMap}
                                    onMeterClick={(el, live) => setMeterPopup({ element: el, liveData: live })}
                                />
                            </Box>
                            {loadingMeters && (
                                <Box sx={{ position: "absolute", top: 14, right: 14, bgcolor: "#1e2330", border: '1px solid rgba(255,255,255,0.08)', borderRadius: 1.5, px: 1.5, py: 0.5, display: "flex", alignItems: "center", gap: 1, boxShadow: '0 2px 12px rgba(0,0,0,0.3)' }}>
                                    <CircularProgress size={13} sx={{ color: '#f59e0b' }} />
                                    <Typography fontSize={11} color="#94a3b8" fontFamily="monospace">Fetching meter data...</Typography>
                                </Box>
                            )}
                            <MeterDetailPopup open={Boolean(meterPopup)} onClose={() => setMeterPopup(null)} element={meterPopup?.element} liveData={meterPopup?.liveData} />
                        </>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                            <ElectricBoltIcon sx={{ fontSize: 52, color: "#94a3b8", opacity: 0.25 }} />
                            <Typography color="#64748b" fontWeight={600} fontSize={14}>
                                {selectedClientId ? "No diagrams for this client" : "Select a client to view diagrams"}
                            </Typography>
                            {selectedClientId && (
                                <Button variant="outlined" startIcon={<AddIcon />} onClick={() => { setNewName(""); setCreateOpen(true); }}
                                    sx={{ textTransform: "none", borderRadius: 1.5, borderColor: 'rgba(255,255,255,0.1)', color: '#94a3b8' }}>
                                    Create first diagram
                                </Button>
                            )}
                        </Box>
                    )}
                </Box>

                {showSidebar && selected && (
                    <RightSidebar meterDataMap={meterDataMap} elements={selected.elements} loading={loadingMeters} onRefresh={refreshMeterData} lastRefreshTime={lastRefreshTime} />
                )}
            </Box>

            {/* ══ Dialogs ══ */}
            {/* ══ Dialogs ══ */}
            <Dialog
                open={createOpen}
                onClose={() => {
                    setCreateOpen(false);
                    setNewName("");
                    setCreateClientId(null);
                }}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { bgcolor: '#1e2330', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, pb: 1, color: '#e2e8f0', fontSize: 15 }}>
                    Create New Diagram
                </DialogTitle>
                <DialogContent>
                    {/* Client Selection Dropdown */}
                    <Box sx={{ mb: 2 }}>
                        <Typography fontSize={12} fontWeight={600} color="#64748b" sx={{ mb: 0.5 }}>
                            Select Client / Site
                        </Typography>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            value={createClientId || ""}
                            onChange={(e) => setCreateClientId(e.target.value)}
                            SelectProps={{
                                native: true,
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: 'rgba(0,0,0,0.2)',
                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                    '&.Mui-focused fieldset': { borderColor: 'rgba(245,158,11,0.5)' }
                                },
                                '& .MuiInputBase-input': { color: '#e2e8f0' }
                            }}
                        >
                            <option value="" disabled>Select a client</option>
                            {/* Show ALL clients that the user has access to, not just those with diagrams */}
                            {clientList.map((client) => (
                                <option key={client.client_id} value={client.client_id}>
                                    {client.company_name || `Client ${client.client_id}`}
                                </option>
                            ))}
                        </TextField>
                    </Box>

                    {/* Diagram Name Input */}
                    <TextField
                        autoFocus
                        fullWidth
                        label="Diagram name"
                        value={newName}
                        onChange={e => setNewName(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") handleCreate(); }}
                        size="small"
                        sx={{
                            mt: 1,
                            '& .MuiOutlinedInput-root': {
                                bgcolor: 'rgba(0,0,0,0.2)',
                                '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                '&.Mui-focused fieldset': { borderColor: 'rgba(245,158,11,0.5)' }
                            },
                            '& .MuiInputLabel-root': { color: '#64748b' },
                            '& .MuiInputBase-input': { color: '#e2e8f0' }
                        }}
                        placeholder={`Diagram ${new Date().toLocaleDateString()}`}
                    />

                    {/* Hint message */}
                    <Typography fontSize={10} color="#475569" sx={{ mt: 1.5, textAlign: 'center' }}>
                        Diagram will be created under the selected client
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => {
                            setCreateOpen(false);
                            setNewName("");
                            setCreateClientId(null);
                        }}
                        sx={{ textTransform: "none", color: '#64748b' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        disabled={!newName.trim() || !createClientId || creating}
                        onClick={handleCreate}
                        sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#f59e0b", color: '#1a1a1a', "&:hover": { bgcolor: "#d97706" } }}
                    >
                        {creating ? <CircularProgress size={16} sx={{ color: "#1a1a1a" }} /> : "Create & Open"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { bgcolor: '#1e2330', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2 } }}>
                <DialogTitle sx={{ fontWeight: 700, pb: 1, color: '#e2e8f0', fontSize: 15 }}>Rename Diagram</DialogTitle>
                <DialogContent>
                    <TextField autoFocus fullWidth label="New name" value={renameName} onChange={e => setRenameName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleRename(); }} size="small"
                        sx={{ mt: 0.5, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(0,0,0,0.2)', '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' }, '&.Mui-focused fieldset': { borderColor: 'rgba(245,158,11,0.5)' } }, '& .MuiInputLabel-root': { color: '#64748b' }, '& .MuiInputBase-input': { color: '#e2e8f0' } }} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setRenameOpen(false)} sx={{ textTransform: "none", color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" disableElevation disabled={!renameName.trim()} onClick={handleRename}
                        sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#f59e0b", color: '#1a1a1a', "&:hover": { bgcolor: "#d97706" } }}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { bgcolor: '#1e2330', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2 } }}>
                <DialogTitle sx={{ fontWeight: 700, color: '#e2e8f0', fontSize: 15 }}>Delete Diagram</DialogTitle>
                <DialogContent>
                    <Typography color="#94a3b8" fontSize={13}>Are you sure you want to delete <strong style={{ color: '#e2e8f0' }}>"{deleteConfirm?.name}"</strong>? This cannot be undone.</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteConfirm(null)} sx={{ textTransform: "none", color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" disableElevation color="error" onClick={handleDelete} sx={{ textTransform: "none", fontWeight: 700 }}>Delete</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={2500} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                <Alert severity={snackbar.severity} variant="filled" sx={{ fontSize: 13 }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}